import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as faceapi from '@vladmandic/face-api';
import { GlossaryTerm } from './GlossaryTerm.jsx';
import { createBiometricSession, analyzeFaceFrame } from '../utils/biometricAnalysis.js';

/**
 * Rutas candidatas a `public/models` (Vite sirve estáticos bajo BASE_URL).
 * loadFromUri suele ir mejor con path del mismo origen (`/models` o `/app/models`) que con URL absoluta.
 */
function getCandidateModelRoots() {
    const base = import.meta.env.BASE_URL || '/';
    const roots = [];
    if (!base || base === '/') {
        roots.push('/models');
    } else {
        roots.push(`${String(base).replace(/\/$/, '')}/models`);
    }
    if (roots[0] !== '/models') {
        roots.push('/models');
    }
    // Si `public/models` no está poblado, mismo formato que el repo upstream (CORS *).
    roots.push('https://raw.githubusercontent.com/vladmandic/face-api/master/model');
    return [...new Set(roots)];
}

async function probeModelJson(root) {
    const base = root.endsWith('/') ? root.slice(0, -1) : root;
    const urls = [
        `${base}/tiny_face_detector_model-weights_manifest.json`,
        `${base}/tiny_face_detector_model.json`,
    ];
    for (const url of urls) {
        try {
            const isRemote = /^https?:\/\//i.test(url);
            const fetchOpts = {
                cache: 'reload',
                credentials: isRemote ? 'omit' : 'same-origin',
            };
            // eslint-disable-next-line no-await-in-loop
            const res = await fetch(url, fetchOpts);
            if (!res.ok) continue;
            // eslint-disable-next-line no-await-in-loop
            const text = await res.text();
            const t = text.trimStart().slice(0, 1);
            if (t === '[' || t === '{') return true;
        } catch {
            /* siguiente candidato */
        }
    }
    return false;
}

/**
 * BiometricScanner - ALINEA Vision Artificial
 * Captures blinking, eyelid opening, somatic tension, and expressions.
 * Implements Privacy-First (processing in-client, no storage).
 */
/**
 * @param {boolean} [metricsOnly=false] Operarios: solo índices numéricos, sin texto interpretativo.
 */
const BiometricScanner = ({ onDataCaptured, metricsOnly = false }) => {
    const videoRef = useRef(null);
    const latestCaptureRef = useRef(null);
    const [status, setStatus] = useState('Initializing Vision...');
    const [biometrics, setBiometrics] = useState({
        tension: 0,
        feeling: 'bien',
        feelingLabel: 'Estable',
        feedbackText: '',
        dominantExpression: 'neutral',
        dominantScore: 0,
        stressScore: 0,
        isConsistent: true,
    });
    const sessionRef = useRef(createBiometricSession());

    const [scanProgress, setScanProgress] = useState(0);
    const [isDone, setIsDone] = useState(false);
    const [modelsReady, setModelsReady] = useState(false);
    const [modelsFailed, setModelsFailed] = useState(false);
    const [loadAttempt, setLoadAttempt] = useState(0);

    useEffect(() => {
        if (scanProgress >= 100 && !isDone) {
            setIsDone(true);
            setStatus('Sincronizado ✓');
            if (videoRef.current && videoRef.current.srcObject) {
                videoRef.current.srcObject.getTracks().forEach(track => track.stop());
            }
            setTimeout(() => {
                if (onDataCaptured) onDataCaptured(latestCaptureRef.current || biometrics);
            }, 800);
        }
    }, [scanProgress, isDone, biometrics, onDataCaptured]);

    const detectIntervalRef = useRef(null);

    useEffect(() => {
        return () => {
            if (detectIntervalRef.current) {
                clearInterval(detectIntervalRef.current);
                detectIntervalRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (isDone && detectIntervalRef.current) {
            clearInterval(detectIntervalRef.current);
            detectIntervalRef.current = null;
        }
    }, [isDone]);

    const startVideo = useCallback(() => {
        navigator.mediaDevices
            .getUserMedia({ video: {} })
            .then((stream) => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }
            })
            .catch(() => setStatus('No se pudo abrir la cámara.'));
    }, []);

    useEffect(() => {
        let cancelled = false;
        const loadModels = async () => {
            setModelsFailed(false);
            setModelsReady(false);
            setStatus('Cargando modelos…');
            const roots = getCandidateModelRoots();
            let workingRoot = null;
            for (const root of roots) {
                if (cancelled) return;
                // eslint-disable-next-line no-await-in-loop
                const ok = await probeModelJson(root);
                if (ok) {
                    workingRoot = root;
                    break;
                }
            }
            if (!workingRoot) {
                if (!cancelled) {
                    setModelsFailed(true);
                    setStatus('Cámara opcional: modelos no disponibles en esta URL.');
                }
                return;
            }
            try {
                await faceapi.nets.tinyFaceDetector.loadFromUri(workingRoot);
                await faceapi.nets.faceLandmark68Net.loadFromUri(workingRoot);
                await faceapi.nets.faceExpressionNet.loadFromUri(workingRoot);
                if (cancelled) return;
                setModelsReady(true);
                setModelsFailed(false);
                setStatus('Listo: centra el rostro');
                startVideo();
            } catch (err) {
                const msg = err?.message || String(err);
                const looksLikeHtml =
                    msg.includes('Unexpected token') ||
                    msg.includes('<!DOCTYPE') ||
                    msg.includes('is not valid JSON');
                if (!looksLikeHtml) {
                    console.error('Model Error:', err);
                }
                if (!cancelled) {
                    setModelsFailed(true);
                    setModelsReady(false);
                    setStatus(
                        looksLikeHtml
                            ? 'Los modelos no cargaron (a veces el caché o la red sirven HTML en vez del archivo). Puedes usar el formulario sin cámara.'
                            : 'Error al cargar modelos.'
                    );
                }
            }
        };
        loadModels();
        return () => {
            cancelled = true;
        };
    }, [loadAttempt, startVideo]);

    const retryModels = () => {
        setScanProgress(0);
        setIsDone(false);
        setModelsReady(false);
        setModelsFailed(false);
        sessionRef.current = createBiometricSession();
        setLoadAttempt((n) => n + 1);
    };

    const handleVideoPlay = () => {
        if (detectIntervalRef.current) {
            clearInterval(detectIntervalRef.current);
            detectIntervalRef.current = null;
        }
        detectIntervalRef.current = setInterval(async () => {
            if (!videoRef.current || isDone) return;
            try {
                const detections = await faceapi
                    .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
                    .withFaceLandmarks()
                    .withFaceExpressions();

                if (detections) {
                    setStatus('Calibrando tu presencia...');
                    setScanProgress((p) => Math.min(p + 2, 100));
                    processDetections(detections);
                } else {
                    setStatus('Centra tu rostro en el espejo...');
                }
            } catch {
                setStatus('Leyendo rostro… reintenta centrar la cara.');
            }
        }, 100);
    };

    const processDetections = (detections) => {
        const data = analyzeFaceFrame(detections, sessionRef.current);
        latestCaptureRef.current = { ...data };
        setBiometrics((prev) => ({ ...prev, ...data }));
    };

    return (
        <div className={`biometric-scanner glass-card${modelsFailed ? ' biometric-scanner--failed' : ''}`}>
            <div className="scanner-header">
                <span className={`status-dot ${status === 'Calibrando tu presencia...' ? 'active' : ''}`} />
                <p className="scanner-status">{status}</p>
            </div>

            {modelsFailed && (
                <div className="biometric-fallback">
                    <p className="biometric-fallback-text">
                        El resto del observador (chips y lectura) sigue igual. Si acabas de actualizar la app, prueba recargar sin
                        caché o «Reintentar».
                    </p>
                    <button type="button" className="biometric-retry" onClick={retryModels}>
                        Reintentar modelos
                    </button>
                </div>
            )}

            {modelsReady && (
                <>
                    <div className="video-wrapper">
                        <video ref={videoRef} autoPlay muted onPlay={handleVideoPlay} className="biometric-video" />
                        <div className="biometric-overlay">
                            <div className="scan-line" />
                            <div className="progress-container">
                                <div className="progress-bar" style={{ width: `${scanProgress}%` }} />
                            </div>
                        </div>
                    </div>

                    {metricsOnly ? (
                        <>
                            <p className="biometric-metrics-only-note text-xs text-muted">
                                Calibración numérica (solo en tu dispositivo). No es evaluación ni diagnóstico.
                            </p>
                            <div className="biometric-metrics">
                                <div className="metric">
                                    <span>Índice T1</span>
                                    <strong>{biometrics.tension ?? '—'}</strong>
                                </div>
                                <div className="metric">
                                    <span>Índice T2</span>
                                    <strong>{biometrics.stressScore ?? '—'}</strong>
                                </div>
                                <div className="metric">
                                    <span>Índice T3</span>
                                    <strong>{biometrics.activationScore ?? '—'}</strong>
                                </div>
                            </div>
                            <div className="biometric-metrics biometric-metrics-secondary">
                                <div className="metric">
                                    <span>Índice P1</span>
                                    <strong>{biometrics.bpm ?? '—'}</strong>
                                </div>
                                <div className="metric">
                                    <span>Índice P2</span>
                                    <strong>{biometrics.ear ?? '—'}</strong>
                                </div>
                                <div className="metric">
                                    <span>Índice P3</span>
                                    <strong>{biometrics.mouthOpen ?? '—'}</strong>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="biometric-metrics">
                                <div className="metric">
                                    <span>
                                        <GlossaryTerm termId="tension_somatica" label="Tensión somática" />
                                    </span>
                                    <strong>{biometrics.tension ?? '—'}%</strong>
                                </div>
                                <div className="metric">
                                    <span>
                                        <GlossaryTerm termId="biometria" label="Sintonía" />
                                    </span>
                                    <strong className={`feeling-${biometrics.feeling}`}>
                                        {biometrics.feelingLabel || biometrics.feeling}
                                    </strong>
                                </div>
                                <div className="metric">
                                    <span>Expresión dominante</span>
                                    <strong>{biometrics.dominantExpression || '—'}</strong>
                                </div>
                            </div>
                            <div className="biometric-metrics biometric-metrics-secondary">
                                <div className="metric">
                                    <span>Estrés (índice)</span>
                                    <strong>{biometrics.stressScore ?? '—'}%</strong>
                                </div>
                                <div className="metric">
                                    <span>Activación</span>
                                    <strong>{biometrics.activationScore ?? '—'}%</strong>
                                </div>
                                <div className="metric">
                                    <span>Parpadeo/min</span>
                                    <strong>{biometrics.bpm ?? '—'}</strong>
                                </div>
                            </div>
                            {biometrics.feedbackText && (
                                <p className="biometric-feedback" role="status">
                                    {biometrics.feedbackText}
                                </p>
                            )}
                        </>
                    )}
                </>
            )}

            <style>{`
                .biometric-scanner {
                    position: relative;
                    padding: 1rem;
                    border: 2px solid rgba(255,255,255,0.1);
                    overflow: hidden;
                    max-width: 400px;
                    margin: 0 auto;
                }
                .biometric-scanner--failed {
                    max-width: 100%;
                    padding: 0.75rem 0.85rem;
                }
                .scanner-status {
                    margin: 0;
                    font-size: 0.8rem;
                    line-height: 1.4;
                    color: #475569;
                    word-break: break-word;
                }
                .biometric-fallback {
                    margin-top: 0.35rem;
                }
                .biometric-fallback-text {
                    margin: 0 0 0.5rem;
                    font-size: 0.78rem;
                    line-height: 1.45;
                    color: #64748b;
                }
                .biometric-retry {
                    padding: 0.45rem 0.75rem;
                    border-radius: 8px;
                    border: 1px solid #cbd5e1;
                    background: #f8fafc;
                    font: inherit;
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: #334155;
                    cursor: pointer;
                }
                .biometric-retry:hover {
                    background: #f1f5f9;
                }
                .scanner-header {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-bottom: 0.75rem;
                }
                .status-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: #64748b;
                }
                .status-dot.active {
                    background: #22c55e;
                    box-shadow: 0 0 10px #22c55e;
                    animation: pulse 2s infinite;
                }
                .video-wrapper {
                    position: relative;
                    border-radius: 12px;
                    background: #000;
                    aspect-ratio: 4/3;
                    margin-bottom: 1rem;
                    overflow: hidden;
                }
                .biometric-video {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transform: scaleX(-1);
                }
                .biometric-overlay {
                    position: absolute;
                    inset: 0;
                    border: 1px solid rgba(34, 197, 94, 0.2);
                    pointer-events: none;
                }
                .scan-line {
                    position: absolute;
                    width: 100%;
                    height: 2px;
                    background: linear-gradient(to right, transparent, #22c55e, transparent);
                    top: 0;
                    animation: scan 3s linear infinite;
                }
                .biometric-glossary-hint {
                    margin: 0.75rem 0 0;
                    font-size: 0.82rem;
                    line-height: 1.45;
                    color: #64748b;
                    text-align: center;
                }
                .biometric-metrics {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 0.5rem;
                    text-align: center;
                }
                .biometric-metrics-secondary {
                    margin-top: 0.5rem;
                    padding-top: 0.5rem;
                    border-top: 1px solid rgba(0,0,0,0.06);
                }
                .biometric-metrics-only-note {
                    margin: 0 0 0.65rem;
                    text-align: center;
                    line-height: 1.4;
                }
                .biometric-feedback {
                    margin: 0.75rem 0 0;
                    font-size: 0.82rem;
                    line-height: 1.5;
                    color: #334155;
                    text-align: left;
                    padding: 0.65rem 0.75rem;
                    background: rgba(255,255,255,0.65);
                    border-radius: 8px;
                    border-left: 3px solid var(--color-primary, #2a5c4a);
                }
                .metric span {
                    display: block;
                    font-size: 0.7rem;
                    color: #64748b;
                    margin-bottom: 0.25rem;
                }
                .metric strong {
                    font-size: 1rem;
                    color: var(--color-primary);
                }
                .feeling-mal { color: #ef4444 !important; }
                .feeling-bien { color: #22c55e !important; }

                .progress-container {
                    position: absolute;
                    bottom: 0px;
                    left: 0;
                    width: 100%;
                    height: 6px;
                    background: rgba(255,255,255,0.1);
                }
                .progress-bar {
                    height: 100%;
                    background: var(--color-primary);
                    box-shadow: 0 0 15px var(--color-primary);
                    transition: width 0.1s ease;
                }
                @keyframes scan {
                    0% { top: 0; }
                    100% { top: 100%; }
                }
                @keyframes pulse {
                    0% { opacity: 0.5; }
                    50% { opacity: 1; }
                    100% { opacity: 0.5; }
                }
            `}</style>
        </div>
    );
};

export default BiometricScanner;
